// src/tattoo/lib/flash.js
//
// Helpers for the database-backed flash gallery feature.
//
// Files live in the Supabase Storage bucket `flash-images`; metadata
// lives in public.flash (see supabase/migrations/20260603000000_flash.sql).
//
// Path convention inside the bucket: `{shop_id}/{uuid}.{ext}`. The
// leading shop_id segment is required by the bucket's RLS policies so
// they can verify ownership (same shape as studio_photos).
//
// Three audiences for reads:
//
//   listFlashForOwner(shopId)
//     — Management UI. Returns ALL statuses including archived. Owner
//       only (RLS still enforces).
//
//   listFlashForShopPublic(shopId)
//     — Public artist page. Returns available/held/sold (no archived).
//
//   listAvailableFlashGlobal()
//     — Editor flash picker. Joined to shop info (name, slug, currency)
//       so cards can render without an extra query per row. Only
//       available status.
//
// All read helpers fall back to [] on error (logged) so callers don't
// have to guard the render path. Mutation helpers throw so the UI can
// surface the error in a toast.

import { supabase } from "./supabaseClient";

const BUCKET = "flash-images";

// ---------- READS ----------

/**
 * Owner's full flash list (all statuses including archived), in display
 * order. Includes resolved public URL on each row.
 */
export async function listFlashForOwner(shopId) {
  if (!shopId) return [];
  try {
    const { data, error } = await supabase
      .from("flash")
      .select(
        "id, shop_id, storage_path, title, description, price_cents, size_label, status, style_tags, is_one_of_one, is_featured, display_order, created_at, updated_at"
      )
      .eq("shop_id", shopId)
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (error) {
      console.warn("[flash] listForOwner failed", error);
      return [];
    }
    return (data || []).map(decorate);
  } catch (e) {
    console.warn("[flash] listForOwner threw", e);
    return [];
  }
}

/**
 * Public read of one shop's flash, excludes archived. Used on the
 * /artists/<slug> public page.
 */
export async function listFlashForShopPublic(shopId) {
  if (!shopId) return [];
  try {
    const { data, error } = await supabase
      .from("flash")
      .select(
        "id, shop_id, storage_path, title, description, price_cents, size_label, status, style_tags, is_one_of_one, is_featured, display_order, created_at"
      )
      .eq("shop_id", shopId)
      .in("status", ["available", "held", "sold"])
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (error) {
      console.warn("[flash] listForShopPublic failed", error);
      return [];
    }
    return (data || []).map(decorate);
  } catch (e) {
    console.warn("[flash] listForShopPublic threw", e);
    return [];
  }
}

/**
 * Global available-flash list for the editor picker. Joins to shops for
 * the artist name + slug + currency so card rendering doesn't need an
 * extra query per row.
 *
 * Supabase PostgREST embeds the related row under `shops` automatically
 * when we select with a colon syntax. We rename it to `shop` for
 * readability in the consumer.
 */
export async function listAvailableFlashGlobal() {
  try {
    const { data, error } = await supabase
      .from("flash")
      .select(
        `
          id, shop_id, storage_path, title, description, price_cents,
          size_label, status, style_tags, is_one_of_one, is_featured, created_at,
          shop:shops!inner(
            id, slug, display_name, name, currency, is_active
          )
        `
      )
      .eq("status", "available")
      .eq("shop.is_active", true)
      .order("created_at", { ascending: false });

    if (error) {
      console.warn("[flash] listAvailableGlobal failed", error);
      return [];
    }
    return (data || []).map(decorate);
  } catch (e) {
    console.warn("[flash] listAvailableGlobal threw", e);
    return [];
  }
}

// ---------- WRITES ----------

/**
 * Upload a file to flash-images and create the metadata row. Returns
 * the inserted row (decorated with .url) on success, throws on failure.
 *
 * fields: optional initial values for the row — title, price_cents,
 *         size_label, status, style_tags, is_one_of_one, is_featured, display_order.
 *         All optional; defaults match the DB column defaults.
 */
export async function uploadFlash(shopId, file, fields = {}) {
  if (!shopId) throw new Error("shopId required");
  if (!file) throw new Error("file required");

  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const storagePath = `${shopId}/${id}.${ext}`;

  // 1. Upload file to storage bucket
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, {
      cacheControl: "31536000",
      upsert: false,
      contentType: file.type || undefined,
    });

  if (upErr) {
    console.error("[flash] upload failed", upErr);
    throw upErr;
  }

  // 2. Insert metadata row. Orphan cleanup on failure so we don't leak
  // bucket space if the metadata write fails.
  const insertPayload = {
    shop_id: shopId,
    storage_path: storagePath,
    title: fields.title ?? null,
    description: fields.description ?? null,
    price_cents:
      typeof fields.price_cents === "number" ? fields.price_cents : null,
    size_label: fields.size_label ?? null,
    style_tags: Array.isArray(fields.style_tags) ? fields.style_tags : null,
  };
  if (typeof fields.is_one_of_one === "boolean") {
    insertPayload.is_one_of_one = fields.is_one_of_one;
  }
  if (typeof fields.display_order === "number") {
    insertPayload.display_order = fields.display_order;
  }

  const { data, error: dbErr } = await supabase
    .from("flash")
    .insert(insertPayload)
    .select(
      "id, shop_id, storage_path, title, description, price_cents, size_label, status, style_tags, is_one_of_one, is_featured, display_order, created_at, updated_at"
    )
    .single();

  if (dbErr || !data) {
    console.error("[flash] metadata insert failed", dbErr);
    try {
      await supabase.storage.from(BUCKET).remove([storagePath]);
    } catch (cleanupErr) {
      console.warn("[flash] orphan cleanup failed", storagePath, cleanupErr);
    }
    throw dbErr || new Error("Could not save flash metadata");
  }

  return decorate(data);
}

/**
 * Partial update on a flash row. Patch only includes the fields you
 * want to change. Returns the updated row.
 *
 * Whitelisted patch keys: title, description, price_cents, size_label,
 * status, style_tags, is_one_of_one, is_featured, display_order.
 */
export async function updateFlash(id, patch) {
  if (!id) throw new Error("id required");
  const allowed = [
    "title",
    "description",
    "price_cents",
    "size_label",
    "status",
    "style_tags",
    "is_one_of_one",
    "display_order",
  ];
  const cleaned = {};
  for (const k of allowed) {
    if (k in patch) cleaned[k] = patch[k];
  }
  if (!Object.keys(cleaned).length) {
    throw new Error("nothing to update");
  }

  const { data, error } = await supabase
    .from("flash")
    .update(cleaned)
    .eq("id", id)
    .select(
      "id, shop_id, storage_path, title, description, price_cents, size_label, status, style_tags, is_one_of_one, is_featured, display_order, created_at, updated_at"
    )
    .single();

  if (error || !data) {
    console.error("[flash] update failed", error);
    throw error || new Error("Could not update flash");
  }
  return decorate(data);
}

/**
 * Delete a flash row + its storage object. Best-effort on the storage
 * delete — if the row is gone but the object remains, the orphan just
 * consumes bucket quota; surfacing that to the user as a "delete
 * failed" toast would be confusing when the row is actually gone.
 */
export async function deleteFlash(id) {
  if (!id) throw new Error("id required");

  // Look up storage_path so we can clean up the file after.
  const { data: row, error: getErr } = await supabase
    .from("flash")
    .select("storage_path")
    .eq("id", id)
    .maybeSingle();

  if (getErr) {
    console.error("[flash] lookup before delete failed", getErr);
    throw getErr;
  }

  const { error: delErr } = await supabase.from("flash").delete().eq("id", id);

  if (delErr) {
    console.error("[flash] delete failed", delErr);
    throw delErr;
  }

  if (row?.storage_path) {
    try {
      await supabase.storage.from(BUCKET).remove([row.storage_path]);
    } catch (e) {
      console.warn(
        "[flash] storage cleanup after row delete failed",
        row.storage_path,
        e
      );
    }
  }
}

// ---------- HELPERS ----------

/** Resolve a storage_path to its public URL via Supabase. */
export function publicUrlFor(storagePath) {
  if (!storagePath) return null;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  return data?.publicUrl || null;
}

/**
 * Format a price for display. Returns "Price on request" when cents is
 * null/undefined, otherwise formats as currency-aware money.
 */
export function formatPrice(cents, currency) {
  if (cents == null) return "Price on request";
  const n = Number(cents);
  if (!Number.isFinite(n)) return "Price on request";
  const dollars = n / 100;
  // Pretty cheap formatter — Intl.NumberFormat would be more correct
  // but adds bundle weight and the only formats we ship today are
  // CAD/USD/EUR/GBP, which all use $ / £ / € prefix conventions.
  const code = (currency || "CAD").toUpperCase();
  const symbol = SYMBOL[code] || "$";
  // Round to nearest whole dollar when the cents are .00; otherwise
  // show two decimals.
  const formatted =
    Number.isInteger(dollars) && dollars >= 1
      ? `${symbol}${dollars}`
      : `${symbol}${dollars.toFixed(2)}`;
  return code === "CAD" || code === "USD" ? formatted : `${formatted}`;
}

const SYMBOL = {
  CAD: "$",
  USD: "$",
  EUR: "€",
  GBP: "£",
};

/**
 * Parse a free-form "$180" / "180" / "180.50" price string into cents.
 * Returns null for empty/invalid input so the UI can store "no price set".
 */
export function parsePriceToCents(input) {
  if (input == null) return null;
  const cleaned = String(input).replace(/[^\d.]/g, "").trim();
  if (!cleaned) return null;
  const num = parseFloat(cleaned);
  if (!Number.isFinite(num)) return null;
  return Math.round(num * 100);
}

/**
 * Mark a flash row as the shop's featured piece. At most one piece per
 * shop can be featured at a time — enforced by the partial unique index
 * flash_one_featured_per_shop_idx, so this function unsets any existing
 * featured before flipping the target. Two writes, no transaction: the
 * second-write window is acceptable because the unique index will
 * reject any concurrent attempt to flip a different row to featured.
 */
export async function setFlashFeatured(id) {
  if (!id) throw new Error("id required");

  // 1. Look up the row's shop_id so we know which scope to clear.
  const { data: target, error: getErr } = await supabase
    .from("flash")
    .select("id, shop_id, is_featured")
    .eq("id", id)
    .single();
  if (getErr || !target) {
    console.error("[flash] setFeatured lookup failed", getErr);
    throw getErr || new Error("Flash not found");
  }

  // Already featured? No-op.
  if (target.is_featured) return await fetchSingleDecorated(id);

  // 2. Unset any existing featured for this shop. Skip the row itself
  // (though it's already false here — defensive).
  const { error: clearErr } = await supabase
    .from("flash")
    .update({ is_featured: false })
    .eq("shop_id", target.shop_id)
    .eq("is_featured", true)
    .neq("id", id);
  if (clearErr) {
    console.error("[flash] setFeatured clear-others failed", clearErr);
    throw clearErr;
  }

  // 3. Flip the target.
  const { data, error } = await supabase
    .from("flash")
    .update({ is_featured: true })
    .eq("id", id)
    .select(
      "id, shop_id, storage_path, title, description, price_cents, size_label, status, style_tags, is_one_of_one, is_featured, display_order, created_at, updated_at"
    )
    .single();
  if (error || !data) {
    console.error("[flash] setFeatured failed", error);
    throw error || new Error("Could not set as featured");
  }
  return decorate(data);
}

/**
 * Clear the featured flag on a single row. Doesn't promote anyone else
 * to featured — the artist's hero falls back to the placeholder until
 * they tag a new one.
 */
export async function unsetFlashFeatured(id) {
  if (!id) throw new Error("id required");
  const { data, error } = await supabase
    .from("flash")
    .update({ is_featured: false })
    .eq("id", id)
    .select(
      "id, shop_id, storage_path, title, description, price_cents, size_label, status, style_tags, is_one_of_one, is_featured, display_order, created_at, updated_at"
    )
    .single();
  if (error || !data) {
    console.error("[flash] unsetFeatured failed", error);
    throw error || new Error("Could not unset featured");
  }
  return decorate(data);
}

async function fetchSingleDecorated(id) {
  const { data, error } = await supabase
    .from("flash")
    .select(
      "id, shop_id, storage_path, title, description, price_cents, size_label, status, style_tags, is_one_of_one, is_featured, display_order, created_at, updated_at"
    )
    .eq("id", id)
    .single();
  if (error || !data) {
    throw error || new Error("Flash not found");
  }
  return decorate(data);
}

function decorate(row) {
  return {
    ...row,
    url: publicUrlFor(row.storage_path),
  };
}
